<template>
  <div>
    <div class="p-box">
      <div class="p-box__body">
        <div class="fileOperation">
          <div class="c-button__item c-button__item--secondary" @click="clickDownloadTemplate">
            {{ $t('pages.CompaniesCustomSurveyAnswerFiles.downloadTemplate') }}
          </div>
          <label for="fileUpload" class="c-button__item c-button__item--secondary">
            {{ $t('pages.CompaniesCustomSurveyAnswerFiles.uploadCsv') }}
            <input id="fileUpload" type="file" name="file" @change="selectFile">
          </label>
        </div>
      </div>
      <div class="line" />
      <div class="p-box__body">
        <Info>
          <i18n-t keypath="pages.CompaniesCustomSurveyAnswerFiles.info.content" tag="div">
            <template #indexLink>
              <router-link
                :to="{ name: 'CompaniesCustomSurveyAnswers', params: { company_uuid: companyUuid }, query: { custom_survey_id: customSurveyId } }"
              >
                {{ $t(`pages.CompaniesCustomSurveyAnswerFiles.info.indexLink`) }}
              </router-link>
            </template>
            <template #analysisLink>
              <router-link :to="{ name: 'CompaniesAnalysisSurveys', params: { company_uuid: companyUuid }, query: { custom_survey_id: customSurveyId } }">
                {{ $t(`pages.CompaniesCustomSurveyAnswerFiles.info.analysisLink`) }}
              </router-link>
            </template>
            <template #keywordLink>
              <router-link :to="{ name: 'CompaniesAnalysisKeywords', params: { company_uuid: companyUuid }, query: { tab: 'store' } }">
                {{ $t(`pages.CompaniesCustomSurveyAnswerFiles.info.keywordLink`) }}
              </router-link>
            </template>
            <template #newline><br></template>
          </i18n-t>
        </Info>
        <Loading v-show="loading" />
        <template v-if="files && !loading">
          <div class="totalCount">
            {{ $t('pages.CompaniesCustomSurveyAnswerFiles.totalCount', { value: numberLocaleString(meta.totalCount) }) }}
          </div>
          <FileTable
            :datas="files"
            :sort-order="sortOrder"
            @delete-file="clickDeleteFile"
            @download-file="clickDownloadFile"
            @click-errors="handleDisplayErrorModal"
            @sort="sort"
          />
          <div v-if="files.length !== 0" class="p-controller">
            <div class="p-controller__count">
              <p class="p-controller__count__name">
                {{ $t('labels.perPageCount') }}
              </p>
              <div class="p-controller__count__select">
                <select :value="displayNumber" @change="changeDisplayNumber">
                  <option v-for="item in 5" :key="item" :value="item * 50">{{ item * 50 }}</option>
                </select>
              </div>
              <p class="p-controller__count__current">{{ startDataNumber }}-{{ endDataNumber }}</p>
            </div>
            <div class="p-controller__navi">
              <button
                type="button"
                :disabled="loading || currentPage === 1"
                class="p-controller__navi__prev"
                @click="goPrevPage"
              >
                <Icon name="arrowLeft" />
              </button>
              <button
                type="button"
                :disabled="loading || currentPage >= meta.totalPages"
                class="p-controller__navi__next"
                @click="goNextPage"
              >
                <Icon name="arrowRight" />
              </button>
            </div>
          </div>
        </template>
      </div>
    </div>
    <ErrorModal
      :show-modal="showErrorModal"
      :errors="errors"
      @close-modal="closeErrorModal"
    />
  </div>
</template>

<script>

import { mapState, mapActions } from 'vuex'
import Loading from '../../../components/Loading'
import FileTable from '../../../components/ImportFiles/Table'
import ErrorModal from '../../../components/CompaniesCustomSurveys/AnswerFiles/ErrorModal'
import { toNumberLocaleString } from '../../../modules/localeString'
import Icon from '../../../components/Icon'
import Info from '../../../components/Info'

export default {
  name: 'CompaniesCustomSurveyAnswerFiles',
  components: {
    Info,
    Loading,
    FileTable,
    ErrorModal,
    Icon,
  },
  emits: ['update-title'],
  data() {
    return {
      currentPage: 1,
      sortOrder: 'desc',
      showErrorModal: false,
      errors: null,
    }
  },
  computed: {
    ...mapState({
      files: state => state.companiesCustomSurveyAnswerFiles.data,
      meta: state => state.companiesCustomSurveyAnswerFiles.meta,
      loading: state => state.companiesCustomSurveyAnswerFiles.loading || state.companiesCustomSurvey.loading,
      customSurvey: state => state.companiesCustomSurvey.data,
      displayNumber: state => state.filters.displayNumber,
    }),
    companyUuid() { return this.$route.params.company_uuid },
    customSurveyId() { return this.$route.params.custom_survey_id },
    startDataNumber() { return (this.currentPage - 1) * this.displayNumber + 1 },
    endDataNumber() { return this.startDataNumber + this.files.length - 1 },
    query() {
      return {
        per: this.displayNumber,
        page: this.currentPage,
        text_type: this.currentTextType,
        sort_order: this.sortOrder,
      }
    }
  },
  async created() {
    this.readFiles({ customSurveyId: this.customSurveyId, query: this.query })
    await this.readCustomSurvey(this.customSurveyId)
    this.$emit(
      'update-title',
      this.$t('pages.CompaniesCustomSurveyAnswerFiles.title', {
        name: this.customSurvey ? this.customSurvey.basic?.templateName : ''
      })
    )
  },
  methods: {
    ...mapActions({
      readCustomSurvey: 'companiesCustomSurvey/readCustomSurvey',
      readFiles: 'companiesCustomSurveyAnswerFiles/readFiles',
      uploadFile: 'companiesCustomSurveyAnswerFiles/uploadFile',
      deleteFile: 'companiesCustomSurveyAnswerFiles/deleteFile',
      downloadTemplate: 'companiesCustomSurveyAnswerFiles/downloadTemplate',
      downloadFile: 'companiesCustomSurveyAnswerFiles/downloadFile',
      setDisplayNumber: 'filters/setDisplayNumber',
    }),
    async selectFile(e) {
      const data = new FormData()
      data.append('custom_survey_answer_file[file]', e.target.files[0])
      data.append('custom_survey_answer_file[custom_survey_id]', 1)
      const isSuccess = await this.uploadFile({customSurveyId: this.customSurveyId, data: data})
      if(isSuccess) this.readFiles({ customSurveyId: this.customSurveyId, query: this.query })
    },
    handleResetPageAndSubmit() {
      this.currentPage = 1
      this.readFiles({ customSurveyId: this.customSurveyId , query: this.query })
    },
    handleScrollTopAndSubmit() {
      window.scrollTo({ top: 0 })
      this.readFiles({ customSurveyId: this.customSurveyId, query: this.query })
    },
    clickDownloadTemplate() {
      this.downloadTemplate(this.customSurveyId)
    },
    clickDownloadFile(item) {
      this.downloadFile({ customSurveyId: this.customSurveyId, id: item.id, filename: item.originalFilename })
    },
    clickDeleteFile(item) {
      if (item.importStatus === 'error' || confirm(this.$t('pages.CompaniesCustomSurveyAnswerFiles.delete.importData'))) {
        this.deleteFile({ customSurveyId: this.customSurveyId, id: item.id })
      }
    },
    sort() {
      if (this.loading) return
      this.sortOrder = this.sortOrder === 'desc' ? 'asc' : 'desc'
      this.handleResetPageAndSubmit()
    },
    /**
     * 表示件数の変更
     * @param  { Object } e
     * @return { Void }
     */
    changeDisplayNumber(e) {
      this.setDisplayNumber(e.target.value)
      this.handleResetPageAndSubmit()
    },
    /**
     * 数値の3桁区切り
     * @param  { Number } value
     * @return { Number }
     */
    numberLocaleString(value) {
      return toNumberLocaleString(value)
    },
    goPrevPage() {
      --this.currentPage
      this.handleScrollTopAndSubmit()
    },
    goNextPage() {
      ++this.currentPage
      this.handleScrollTopAndSubmit()
    },
    closeErrorModal() {
      this.showErrorModal = false
    },
    handleDisplayErrorModal(errors) {
      this.errors = errors
      this.showErrorModal = true
    }
  },
}
</script>

<style lang="scss" scoped>
.fileOperation {
  display: flex;
  padding-top: 10px;
  justify-content: flex-end;
  margin-left: auto;
  width: 30%;
  input {
    display: none;
  }
}
.totalCount {
  margin-bottom: 10px;
  font-weight: bold;
}
.line {
  border-bottom: 1px solid #E4E7EB;
}
.fontNormal {
  font-weight: normal;
}
</style>
